import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { ListingCard } from "@/components/site/ListingCard";
import { RecentPostsWidget } from "@/components/site/RecentPostsWidget";
import {
  blogPosts,
  getCategory,
  getLocation,
  getListing,
  comboHref,
  categories,
  type Listing,
  type BlogPost,
} from "@/lib/data";
import { generateBreadcrumbSchema } from "@/lib/utils";
import { Calendar, Clock, User, Share2, Tag } from "lucide-react";

const SITE_URL = "https://letslistbusiness.com";


export const Route = createFileRoute("/blog/$slug/")({
  loader: ({ params }) => {
    const post = blogPosts.find((p) => p.slug === params.slug);
    if (!post) throw notFound();
    const cat = getCategory(post.category)!;
    const loc = getLocation(post.location)!;
    const related = post.relatedListings.map(getListing).filter(Boolean) as NonNullable<ReturnType<typeof getListing>>[];
    const more = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);
    return { post, cat, loc, related, more };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { post } = loaderData;
    const url = `${SITE_URL}/blog/${post.slug}/`;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      image: post.image,
      datePublished: post.date,
      author: { "@type": "Person", name: post.author },
      publisher: {
        "@type": "Organization",
        name: "LetsListBusiness",
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/logo.png`,
        },
      },
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": url,
      },
    };
    return {
      meta: [
        { title: `${post.title} | LetsListBusiness` },
        { name: "description", content: post.excerpt },
        { property: "og:title", content: post.title },
        { property: "og:description", content: post.excerpt },
        { property: "og:image", content: post.image },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post.title },
        { name: "twitter:description", content: post.excerpt },
        { name: "twitter:image", content: post.image },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        ld,
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Blog", href: "/blog/" },
          { label: post.title, href: url },
        ], SITE_URL),
      ],
    };
  },
  component: BlogPostPage,
  notFoundComponent: () => (
    <div className="container mx-auto px-4 py-20 text-center">
      <h1 className="font-display text-3xl font-bold">Article not found</h1>
      <Link to="/blog" className="mt-4 inline-block text-primary">All articles</Link>
    </div>
  ),
});

function BlogPostPage() {
  const { post, cat, loc, related, more } = Route.useLoaderData();
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Blog", href: "/blog/" }, { label: post.title }]} />

      <main className="container mx-auto px-4 py-8 lg:py-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
          {/* Main Content */}
          <div className="lg:col-span-8">
            <article>
              <header>
                <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-[0.2em] text-primary">
                  <Link to="/category/$slug" params={{ slug: cat.slug }} className="rounded-full bg-primary/10 px-3 py-1 hover:bg-primary/20 transition-colors">
                    {cat.name}
                  </Link>
                  <span className="text-muted-foreground/30">/</span>
                  <Link to="/location/$slug" params={{ slug: loc.slug }} className="hover:underline">{loc.name}</Link>
                </div>
                
                <h1 className="mt-6 font-display text-4xl font-bold leading-[1.1] text-foreground sm:text-5xl lg:text-6xl">
                  {post.title}
                </h1>

                <div className="mt-8 flex flex-wrap items-center gap-6 border-y border-border py-6 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-warm flex items-center justify-center text-[10px] font-bold text-accent-foreground">
                      {post.author.charAt(0)}
                    </div>
                    <span className="font-medium text-foreground">{post.author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 opacity-70" />
                    <span>{post.date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 opacity-70" />
                    <span>{new Date(post.date + "T12:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
                  </div>
                </div>
              </header>

              <div className="mt-10 overflow-hidden rounded-3xl shadow-card-lg">
                <img src={post.image} alt={post.title} className="aspect-video w-full object-cover transition-transform duration-700 hover:scale-105" />
              </div>

              <div className="mt-12 text-lg leading-relaxed text-foreground/85">
                <p className="text-2xl font-display font-medium leading-snug text-foreground/90 mb-8 italic border-l-4 border-primary pl-6">
                  {post.excerpt}
                </p>

                <div className="prose prose-lg max-w-none prose-headings:font-display prose-headings:font-bold prose-a:text-primary prose-a:no-underline hover:prose-a:underline [&>p:first-of-type]:first-letter:float-left [&>p:first-of-type]:first-letter:mr-3 [&>p:first-of-type]:first-letter:font-display [&>p:first-of-type]:first-letter:text-7xl [&>p:first-of-type]:first-letter:font-bold [&>p:first-of-type]:first-letter:leading-[0.85] [&>p:first-of-type]:first-letter:text-primary">
                  {post.content.split("\n\n").map((block: string, i: number) => {
                    if (block.startsWith("## ")) {
                      return (
                        <h2 key={i} className="mb-6 mt-12 font-display text-3xl font-bold text-foreground">
                          {block.replace("## ", "")}
                        </h2>
                      );
                    }
                    const parts = block.split(/(\[[^\]]+\]\([^)]+\))/g);
                    return (
                      <p key={i} className="mb-6">
                        {parts.map((part, index) => {
                          const match = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
                          if (match) {
                            return (
                              <Link
                                key={index}
                                to={match[2] as any}
                                className="text-primary font-semibold underline underline-offset-4 decoration-primary/30 hover:decoration-primary transition-all"
                              >
                                {match[1]}
                              </Link>
                            );
                          }
                          return part;
                        })}
                      </p>
                    );
                  })}
                </div>

                <div className="mt-12 rounded-3xl bg-surface p-8 border border-border shadow-sm">
                   <p className="m-0 italic">
                    Looking for more? Browse all{" "}
                    <Link {...comboHref(loc.slug, cat.slug)} className="text-primary font-bold hover:underline">
                      {cat.name.toLowerCase()} in {loc.name}
                    </Link>
                    , explore other{" "}
                    <Link to="/category/$slug" params={{ slug: cat.slug }} className="text-primary font-bold hover:underline">{cat.name.toLowerCase()}</Link>
                    {" "}or see what's happening across{" "}
                    <Link to="/location/$slug" params={{ slug: loc.slug }} className="text-primary font-bold hover:underline">{loc.name}</Link>.
                  </p>
                </div>
              </div>

              {/* Share & Tags */}
              <div className="mt-12 flex flex-wrap items-center justify-between gap-6 border-t border-border pt-8">
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Tags:</span>
                  <div className="flex gap-2">
                    <Link to="/category/$slug" params={{ slug: cat.slug }} className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium hover:bg-border transition-colors">
                      <Tag className="h-3 w-3" /> {cat.name}
                    </Link>
                    <Link to="/location/$slug" params={{ slug: loc.slug }} className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium hover:bg-border transition-colors">
                      <Tag className="h-3 w-3" /> {loc.name}
                    </Link>
                  </div>
                </div>
                <button className="flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-surface transition-colors">
                  <Share2 className="h-4 w-4" /> Share Article
                </button>
              </div>

              {/* Author box */}
              <div className="mt-12 flex items-center gap-6 rounded-3xl bg-gradient-subtle border border-border p-8">
                <div className="grid h-20 w-20 shrink-0 place-items-center rounded-full bg-gradient-warm font-display text-3xl font-bold text-accent-foreground shadow-glow">
                  {post.author.charAt(0)}
                </div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-primary">About the Author</div>
                  <div className="mt-1 font-display text-2xl font-bold">{post.author}</div>
                  <p className="mt-2 text-muted-foreground leading-relaxed">
                    Local expert and community advocate dedicated to uncovering the hidden gems and best service providers in {loc.name}.
                  </p>
                </div>
              </div>
            </article>

            {/* Mentioned listings */}
            {related.length > 0 && (
              <section className="mt-20">
                <div className="flex items-center justify-between border-b border-border pb-4">
                  <h2 className="font-display text-3xl font-bold">In this article</h2>
                  <span className="rounded-full bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-primary">
                    {related.length} Places
                  </span>
                </div>
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  {related.map((l: Listing) => <ListingCard key={l.id} listing={l} />)}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-4 space-y-10">
            {/* Recent Posts Widget */}
            <RecentPostsWidget currentSlug={post.slug} />

            {/* Categories Widget */}
            <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
              <h3 className="font-display text-lg font-bold">Categories</h3>
              <div className="mt-5 grid grid-cols-1 gap-2">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to="/category/$slug"
                    params={{ slug: c.slug }}
                    className="group flex items-center justify-between rounded-xl p-3 transition-colors hover:bg-surface"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{c.icon}</span>
                      <span className="font-medium group-hover:text-primary transition-colors">{c.name}</span>
                    </div>
                    <span className="text-[10px] font-bold text-muted-foreground/50">
                      {blogPosts.filter(p => p.category === c.slug).length}
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Newsletter / CTA Widget */}
            <div className="rounded-2xl bg-foreground p-8 text-background shadow-glow relative overflow-hidden">
              <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 h-32 w-32 rounded-full bg-white/5 blur-3xl" />
              <div className="relative z-10">
                <h3 className="font-display text-xl font-bold">Stay Updated</h3>
                <p className="mt-2 text-sm text-background/70 leading-relaxed">
                  Get the latest local guides and business recommendations delivered to your inbox.
                </p>
                <form className="mt-6 space-y-3">
                  <input
                    type="email"
                    placeholder="your@email.com"
                    className="w-full rounded-xl bg-white/10 border border-white/20 px-4 py-3 text-sm placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <button className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-primary-foreground hover:bg-primary-glow transition-all">
                    Subscribe
                  </button>
                </form>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* Keep reading (Bottom Section) */}
      <section className="bg-surface py-20">
        <div className="container mx-auto px-4">
          <div className="flex items-end justify-between">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-primary">Continue</div>
              <h2 className="mt-2 font-display text-3xl font-bold">Related Stories</h2>
            </div>
            <Link to="/blog" className="text-sm font-semibold hover:underline">View All Posts</Link>
          </div>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {more.map((p: BlogPost) => (
              <Link key={p.slug} to="/blog/$slug" params={{ slug: p.slug }} className="group block">
                <div className="overflow-hidden rounded-2xl shadow-card">
                  <img src={p.image} alt={p.title} loading="lazy" className="aspect-16/10 w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <h3 className="mt-4 font-display text-xl font-bold leading-tight group-hover:text-primary transition-colors line-clamp-2">{p.title}</h3>
                <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

